import * as React from "react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

const Spinner = React.forwardRef(
  ({ className, size = "default", ...props }, ref) => {
    const sizeClasses = {
      xs: "h-3.5 w-3.5",
      sm: "h-4 w-4",
      default: "h-5 w-5",
      lg: "h-6 w-6",
      xl: "h-8 w-8",
    };

    return (
      <Loader2
        ref={ref}
        className={cn(
          "animate-spin text-sunridge-cyan drop-shadow-[0_0_10px_rgba(31,160,203,0.2)]",
          sizeClasses[size],
          className
        )}
        {...props}
      />
    );
  }
);
Spinner.displayName = "Spinner";

const LoadingMark = React.forwardRef(
  ({ className, size = "default", ...props }, ref) => {
    const shellSize = {
      sm: "h-8 w-8",
      default: "h-10 w-10",
      lg: "h-12 w-12",
    };

    const spinnerSize = size === "lg" ? "lg" : size === "sm" ? "sm" : "default";

    return (
      <div
        ref={ref}
        className={cn(
          "relative inline-flex items-center justify-center rounded-xl",
          "bg-gradient-to-br from-sunridge-cyan/10 via-white to-sunridge-lime/10",
          "border border-sunridge-cyan/15 shadow-sm shadow-sunridge-cyan/10",
          shellSize[size] || shellSize.default,
          className
        )}
        {...props}
      >
        <span className="absolute inset-1 rounded-lg border border-white/70" />
        <Spinner size={spinnerSize} />
      </div>
    );
  }
);
LoadingMark.displayName = "LoadingMark";

const SkeletonLine = ({ className }) => (
  <div className={cn("rounded-full bg-slate-100 animate-pulse", className)} />
);

const PageSectionSkeleton = React.forwardRef(
  ({ className, compact = false, rows = 4, label = "Loading", ...props }, ref) => {
    const rowCount = Array.from({ length: rows });

    return (
      <section
        ref={ref}
        className={cn(
          "w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden",
          className
        )}
        aria-busy="true"
        aria-label={label}
        {...props}
      >
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 bg-slate-50/60 px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <LoadingMark size={compact ? "sm" : "default"} />
            <div className="space-y-2 min-w-0">
              <SkeletonLine className="h-3 w-28" />
              <SkeletonLine className="h-2.5 w-44 max-w-[42vw]" />
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <SkeletonLine className="h-7 w-20" />
            <SkeletonLine className="h-7 w-9" />
          </div>
        </div>

        <div className={cn("p-4", compact ? "space-y-3" : "space-y-4")}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-3 space-y-3">
              <SkeletonLine className="h-3 w-24" />
              <SkeletonLine className="h-3 w-full" />
              <SkeletonLine className="h-3 w-5/6" />
              <SkeletonLine className="h-3 w-2/3" />
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-3 space-y-3">
              <SkeletonLine className="h-3 w-28" />
              <SkeletonLine className="h-3 w-full" />
              <SkeletonLine className="h-3 w-3/4" />
              <SkeletonLine className="h-3 w-4/5" />
            </div>
          </div>

          <div className="space-y-2">
            {rowCount.map((_, index) => (
              <div key={index} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-3 py-2">
                <SkeletonLine className="h-8 w-8 rounded-lg" />
                <div className="flex-1 space-y-2 min-w-0">
                  <SkeletonLine className="h-2.5 w-3/4" />
                  <SkeletonLine className="h-2.5 w-1/2" />
                </div>
                <SkeletonLine className="h-6 w-16" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
);
PageSectionSkeleton.displayName = "PageSectionSkeleton";

const EditorLoadingOverlay = React.forwardRef(
  ({ className, label = "Loading", ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "absolute inset-0 z-20 flex items-center justify-center rounded-xl",
        "bg-white/75 backdrop-blur-[2px]",
        className
      )}
      aria-busy="true"
      aria-label={label}
      {...props}
    >
      <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-2 shadow-sm">
        <Spinner size="sm" />
        <span className="text-xs font-semibold text-slate-500">{label}</span>
      </div>
    </div>
  )
);
EditorLoadingOverlay.displayName = "EditorLoadingOverlay";

// Full page loading spinner with optional message
const LoadingOverlay = React.forwardRef(
  ({ className, message, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "fixed inset-0 z-50 flex items-center justify-center bg-black/50",
          className
        )}
        {...props}
      >
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/95 px-5 py-4 shadow-xl">
          <LoadingMark size="lg" />
          {message && (
            <p className="m-0 text-sm text-slate-600 font-semibold">{message}</p>
          )}
        </div>
      </div>
    );
  }
);
LoadingOverlay.displayName = "LoadingOverlay";

// Inline loading indicator
const InlineSpinner = React.forwardRef(
  ({ className, text = "Loading...", ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("inline-flex items-center gap-2 text-slate-500", className)}
        {...props}
      >
        <Spinner size="sm" />
        <span className="text-sm font-medium">{text}</span>
      </div>
    );
  }
);
InlineSpinner.displayName = "InlineSpinner";

export { Spinner, LoadingMark, LoadingOverlay, InlineSpinner, PageSectionSkeleton, EditorLoadingOverlay };
